import { Link } from "react-router-dom";
import { BarChart3, Bell, ChevronRight, CircleHelp, HandCoins, History, IdCard, LogOut, Settings, UserCog, UserRound, Users, type LucideIcon } from "lucide-react";
import { resolveAssetUrl } from "@/lib/api-client";
import { useAppStore } from "@/store/app-store";
import { usePropertyAccess, type Permission } from "@/lib/property-access";
import { initials } from "@/lib/format";

type MoreItem = { to?: string; href?: string; icon: LucideIcon; label: string; hint: string; adminOnly?: boolean; permission?: Permission };

// Everything the mobile bottom nav doesn't hold, grouped by what the owner is trying to do.
const groups: { title: string; items: MoreItem[] }[] = [
  {
    title: "Operate",
    items: [
      { to: "/guests", icon: Users, label: "Guests", hint: "Profiles from your booking history" },
      { to: "/reports", icon: BarChart3, label: "Reports", hint: "Revenue, bookings and sources", permission: "REPORTS" },
      { to: "/settlements", icon: HandCoins, label: "Settlements", hint: "App payments paid out to you", permission: "SETTLEMENTS" },
      { to: "/police-register", icon: IdCard, label: "Police register", hint: "Guest IDs - Excel / PDF for the police", permission: "GUEST_IDS" },
      { to: "/activity", icon: History, label: "Activity log", hint: "Who did what on the bookings", permission: "REPORTS" },
      { to: "/team", icon: UserCog, label: "Team & access", hint: "Who works on this property and what they can do" },
    ],
  },
  {
    title: "Account",
    items: [
      { to: "/settings/general", icon: Settings, label: "Property settings", hint: "Details, rooms, pricing and policies", adminOnly: true },
      { to: "/notifications", icon: Bell, label: "Notifications", hint: "Booking alerts" },
      { to: "/profile", icon: UserRound, label: "My profile", hint: "Name, photo and password" },
      { href: "mailto:support@darshan.in", icon: CircleHelp, label: "Help & support", hint: "support@darshan.in" },
    ],
  },
];

export function MorePage() {
  const user = useAppStore((state) => state.user);
  const activeProperty = useAppStore((state) => state.activeProperty);
  const clearSession = useAppStore((state) => state.clearSession);
  const isAdmin = user?.role === "ADMIN";
  const access = usePropertyAccess();

  return (
    <main className="page more-page">
      <header className="page-head resource-head">
        <h1>More</h1>
      </header>

      <Link to="/profile" className="more-profile-card">
        {user?.avatarUrl ? <img className="avatar" src={resolveAssetUrl(user.avatarUrl)} alt="" /> : <span className="avatar">{initials(user?.name)}</span>}
        <span className="more-profile-card__text">
          <strong>{user?.name}</strong>
          <small>{isAdmin ? "Administrator" : "Property owner"}{activeProperty ? ` · ${activeProperty.name}` : ""}</small>
        </span>
        <ChevronRight size={18} aria-hidden="true" />
      </Link>

      {groups.map((group) => {
        const items = group.items.filter((item) => (!item.adminOnly || isAdmin) && (!item.permission || access.can(item.permission)));
        return (
          <section key={group.title} className="more-group" aria-labelledby={`more-${group.title}`}>
            <h2 id={`more-${group.title}`} className="section-label">{group.title}</h2>
            <div className="card more-list">
              {items.map((item) => {
                const content = (
                  <>
                    <span className="more-list__icon"><item.icon size={18} aria-hidden="true" /></span>
                    <span className="more-list__text"><strong>{item.label}</strong><small>{item.hint}</small></span>
                    <ChevronRight size={18} aria-hidden="true" />
                  </>
                );
                return item.to
                  ? <Link key={item.label} to={item.to} className="more-list__row">{content}</Link>
                  : <a key={item.label} href={item.href} className="more-list__row">{content}</a>;
              })}
            </div>
          </section>
        );
      })}

      <button type="button" className="button button--secondary button--danger-outline more-signout" onClick={clearSession}>
        <LogOut size={18} /> Sign out
      </button>
    </main>
  );
}
